import React, { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { SHORT_TERM_GOALS } from "../lib/site";
import { ChevronLeft, ChevronRight, ArrowRight, Zap } from "lucide-react";

export default function Roadmap() {
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const updateActive = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const itemWidth = el.scrollWidth / SHORT_TERM_GOALS.length;
    const idx = Math.round(el.scrollLeft / itemWidth);
    setActiveIndex(Math.min(Math.max(idx, 0), SHORT_TERM_GOALS.length - 1));
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateActive, { passive: true });
    return () => el.removeEventListener("scroll", updateActive);
  }, [updateActive]);

  const scrollTo = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const itemWidth = el.scrollWidth / SHORT_TERM_GOALS.length;
    el.scrollTo({ left: idx * itemWidth, behavior: "smooth" });
  };

  const scrollByDir = (dir) => {
    const next = Math.min(
      Math.max(activeIndex + dir, 0),
      SHORT_TERM_GOALS.length - 1,
    );
    scrollTo(next);
  };

  return (
    <section
      id="roadmap"
      data-testid="roadmap-section"
      className="relative py-20 sm:py-28 bg-[#06281F] text-white overflow-hidden"
    >
      {/* Ambient glows */}
      <div className="absolute -top-40 left-1/4 h-80 w-80 rounded-full bg-[#0b6b4f] blur-3xl opacity-40 pointer-events-none" />
      <div className="absolute -bottom-40 right-1/4 h-80 w-80 rounded-full bg-[#7c5e10] blur-3xl opacity-20 pointer-events-none" />
      <div className="absolute inset-0 geo-overlay-light opacity-25 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14 sm:mb-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/8 border border-white/15 backdrop-blur-md px-3 py-1.5 mb-5">
              <Zap size={12} className="text-[#FCD34D]" />
              <span className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#FCD34D]">
                Roadmap • 2025 – 2027
              </span>
            </div>
            <h2 className="font-serif-display text-3xl sm:text-5xl leading-[1.1] font-semibold">
              The road we're{" "}
              <span className="italic text-[#FCD34D]">walking</span>.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-white/75 leading-relaxed">
              Six concrete milestones — slide through them, insha'Allah.
            </p>
          </div>

          <Link
            to="/mission"
            data-testid="roadmap-full-plan"
            className="inline-flex items-center gap-2 rounded-full bg-white/8 hover:bg-white/14 border border-white/20 backdrop-blur-md text-white px-5 py-2.5 text-xs sm:text-sm font-semibold transition-all w-fit"
          >
            See the full strategic plan
            <ArrowRight size={14} />
          </Link>
        </div>

        {/* === TIMELINE === */}
        <div className="relative grid grid-cols-1 sm:grid-cols-[56px_1fr] gap-6 sm:gap-10">
          {/* Left vertical index (01–06) */}
          <aside
            data-testid="roadmap-index"
            className="hidden sm:block relative pt-2"
          >
            <div className="absolute left-[10px] top-2 bottom-2 w-px bg-white/15" />
            <ul className="space-y-7">
              {SHORT_TERM_GOALS.map((_, i) => (
                <li key={i} className="relative">
                  <button
                    onClick={() => scrollTo(i)}
                    data-testid={`roadmap-index-${i}`}
                    aria-label={`Go to milestone ${i + 1}`}
                    className="flex items-center gap-3 group"
                  >
                    <span
                      className={`h-2.5 w-2.5 rounded-full transition-all ${
                        i === activeIndex
                          ? "bg-[#DC2626] shadow-[0_0_14px_rgba(220,38,38,0.7)]"
                          : "bg-white/30 group-hover:bg-white/60"
                      }`}
                    />
                    <span
                      className={`text-[11px] font-semibold tracking-[0.18em] transition-colors ${
                        i === activeIndex
                          ? "text-[#FCD34D]"
                          : "text-white/40 group-hover:text-white/70"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Right scroller */}
          <div className="relative">
            {/* Horizontal scroll viewport */}
            <div
              ref={scrollerRef}
              data-testid="roadmap-scroller"
              className="relative overflow-x-auto overflow-y-hidden scroll-smooth snap-x snap-mandatory scrollbar-hide pb-2"
              style={{ scrollbarWidth: "none" }}
            >
              {/* horizontal line behind dots — sized to scrollWidth */}
              <div className="relative min-w-full">
                <div className="absolute left-0 right-0 top-[88px] sm:top-[100px] h-px bg-white/15" />
                <div
                  className="absolute left-0 top-[88px] sm:top-[100px] h-[2px] bg-gradient-to-r from-[#FCD34D] to-[#DC2626] transition-all duration-500 ease-out"
                  style={{
                    width: `${((activeIndex + 1) / SHORT_TERM_GOALS.length) * 100}%`,
                  }}
                />

                <div className="flex gap-0">
                  {SHORT_TERM_GOALS.map((g, i) => {
                    const isActive = i === activeIndex;
                    return (
                      <article
                        key={g.goal}
                        data-testid={`roadmap-item-${i}`}
                        className="snap-start shrink-0 w-[85%] sm:w-1/2 lg:w-1/3 px-2 sm:px-4"
                      >
                        {/* Top — timeline */}
                        <div className="text-center h-[70px] sm:h-[80px] flex items-end justify-center pb-3">
                          <span
                            className={`font-serif-display text-2xl sm:text-3xl font-semibold tracking-wide transition-colors ${
                              isActive ? "text-[#DC2626]" : "text-white/50"
                            }`}
                          >
                            {g.timeline}
                          </span>
                        </div>

                        {/* The dot on the line */}
                        <div className="flex items-center justify-center h-6">
                          <div
                            className={`relative rounded-full transition-all duration-300 ${
                              isActive
                                ? "h-5 w-5 bg-[#DC2626] shadow-[0_0_24px_rgba(220,38,38,0.7)]"
                                : "h-3 w-3 bg-white/40"
                            }`}
                          >
                            {isActive && (
                              <span className="absolute inset-0 rounded-full border-2 border-[#DC2626]/40 animate-ping" />
                            )}
                          </div>
                        </div>

                        {/* Below — goal title + content */}
                        <div className="mt-6 sm:mt-8 text-center px-1 sm:px-2">
                          <div
                            className={`text-[10px] sm:text-[11px] uppercase tracking-[0.24em] font-semibold transition-colors ${
                              isActive ? "text-[#FCD34D]" : "text-white/40"
                            }`}
                          >
                            Phase {String(i + 1).padStart(2, "0")}
                          </div>
                          <h3
                            className={`font-serif-display text-xl sm:text-2xl font-semibold mt-3 leading-tight transition-colors ${
                              isActive ? "text-white" : "text-white/50"
                            }`}
                          >
                            {g.goal}
                          </h3>
                          <p
                            className={`mt-3 text-[13px] sm:text-sm leading-relaxed mx-auto max-w-xs transition-colors ${
                              isActive ? "text-white/80" : "text-white/35"
                            }`}
                          >
                            {g.actions}
                          </p>
                          <div
                            className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.16em] font-semibold transition-all ${
                              isActive
                                ? "bg-[#FCD34D]/15 text-[#FCD34D]"
                                : "bg-white/5 text-white/40"
                            }`}
                          >
                            KPI · {g.kpi}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Controls + counter */}
            <div className="mt-8 sm:mt-10 flex items-center justify-between gap-4">
              <div className="text-xs text-white/50 font-semibold tracking-wider tabular-nums">
                <span className="text-[#FCD34D] text-base font-serif-display">
                  {String(activeIndex + 1).padStart(2, "0")}
                </span>{" "}
                <span className="text-white/30">/</span>{" "}
                {String(SHORT_TERM_GOALS.length).padStart(2, "0")}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => scrollByDir(-1)}
                  disabled={activeIndex === 0}
                  data-testid="roadmap-prev"
                  aria-label="Previous milestone"
                  className="h-11 w-11 rounded-full border border-white/25 bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center transition-all"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => scrollByDir(1)}
                  disabled={activeIndex === SHORT_TERM_GOALS.length - 1}
                  data-testid="roadmap-next"
                  aria-label="Next milestone"
                  className="h-11 w-11 rounded-full border border-white/25 bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center transition-all"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
}
